import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import GlassCard from "../../components/dashboard/GlassCard";
import saasApi from "../../utils/saasApi";

export const currency = (amount, code = "ARS") => amount === null || amount === undefined || amount === "" || !Number.isFinite(Number(amount))
  ? "—" : new Intl.NumberFormat("es-AR", { style: "currency", currency: code, maximumFractionDigits: 2 }).format(Number(amount));
const formatDate = (date) => date ? new Date(date).toLocaleString("es-AR") : "Sin fecha registrada";

const Billing = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [cameras, setCameras] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [billing, setBilling] = useState(null);
  const [selection, setSelection] = useState({});
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startingSubscription, setStartingSubscription] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const quoteRequestId = useRef(0);

  useEffect(() => {
    let live = true;
    Promise.all([saasApi.getBillingSetupCameras(), saasApi.getDetectionConfig(), saasApi.getBillingCatalog(), saasApi.getBillingState()])
      .then(([cameraResult, configResult, catalogResult, billingResult]) => {
        if (!live) return;
        const nextCameras = cameraResult.cameras || [];
        const nextConfigs = configResult.configs || [];
        const nextSelection = {};
        nextCameras.forEach((camera) => {
          const current = (billingResult.items || []).filter((item) => Number(item.camera_id) === Number(camera.id));
          const selectedDetections = current.length
            ? current.filter((item) => item.component_type === "detection").map((item) => item.detection_type)
            : nextConfigs.filter((item) => Number(item.camera_id) === Number(camera.id) && item.enabled).map((item) => item.detection_type);
          nextSelection[camera.id] = {
            included: current.length ? current.some((item) => item.component_type === "camera") : true,
            detections: [...new Set(selectedDetections)],
          };
        });
        setCameras(nextCameras); setConfigs(nextConfigs); setCatalog(catalogResult); setBilling(billingResult); setSelection(nextSelection); setError("");
      }).catch((err) => { if (live) setError(err.message || "No se pudo cargar la configuracion comercial"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const requestedCameras = useMemo(() => cameras.filter((camera) => selection[camera.id]?.included)
    .map((camera) => ({ camera_id: Number(camera.id), detections: selection[camera.id]?.detections || [] })), [cameras, selection]);

  const loadQuote = useCallback(async (selected) => {
    const requestId = ++quoteRequestId.current;
    if (!selected.length) { setQuote(null); setQuoting(false); return; }
    setQuoting(true);
    try {
      const next = await saasApi.quoteBillingSelection({ cameras: selected });
      if (requestId === quoteRequestId.current) { setQuote(next); setError(""); }
    } catch (err) {
      if (requestId === quoteRequestId.current) { setQuote(null); setError(err.message); }
    } finally { if (requestId === quoteRequestId.current) setQuoting(false); }
  }, []);

  useEffect(() => {
    quoteRequestId.current += 1;
    setQuote(null);
    setQuoting(false);
    const timer = setTimeout(() => { if (catalog) loadQuote(requestedCameras); }, 250);
    return () => { clearTimeout(timer); quoteRequestId.current += 1; };
  }, [catalog, requestedCameras, loadQuote]);

  const toggleCamera = (cameraId, included) => setSelection((previous) => ({ ...previous, [cameraId]: { ...previous[cameraId], included } }));
  const toggleDetection = (cameraId, detection, checked) => setSelection((previous) => {
    const current = previous[cameraId] || { included: true, detections: [] };
    const detections = checked ? [...new Set([...current.detections, detection])] : current.detections.filter((item) => item !== detection);
    return { ...previous, [cameraId]: { ...current, detections } };
  });

  const submitRequest = async () => {
    setSubmitting(true); setError(""); setNotice("");
    try {
      const result = await saasApi.createBillingChangeRequest({ cameras: requestedCameras });
      setBilling((current) => ({ ...current, change_requests: [
        { id: result.id, status: result.status, quote_snapshot: result.quote, created_at: new Date().toISOString() },
        ...(current?.change_requests || []),
      ] }));
      setNotice(t(initialContract ? "subscriptionBilling.initialRequestSent" : "subscriptionBilling.changeRequestSent"));
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const startSubscription = async () => {
    setStartingSubscription(true); setError("");
    try {
      const result = await saasApi.startBillingSubscription();
      window.location.assign(result.checkout_url);
    } catch (err) { setError(err.message); setStartingSubscription(false); }
  };

  const activeItemsByCamera = useMemo(() => {
    const map = new Map();
    (billing?.items || []).forEach((item) => map.set(Number(item.camera_id), [...(map.get(Number(item.camera_id)) || []), item]));
    return map;
  }, [billing]);
  const priceByKey = useMemo(() => new Map((catalog?.prices || []).map((price) => [`${price.component_type}:${price.detection_type || ""}`, price.amount])), [catalog]);
  const requestInProgress = (billing?.change_requests || []).some((request) => ["pending", "processing", "approved"].includes(request.status));
  const initialContract = !(billing?.items || []).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Header title={t("subscriptionBilling.title")} subtitle={t("subscriptionBilling.subtitle")} />
      <Stack spacing={2}>
        {location.search.includes("payment_return") && <Alert severity="info">{t("subscriptionBilling.paymentReturn")}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}{notice && <Alert severity="success">{notice}</Alert>}
        {billing?.contract?.subscription_status === "legacy_review" && <Alert severity="warning">{t("subscriptionBilling.legacyReview")}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><GlassCard hover={false}>
            <Typography variant="subtitle2" color="text.secondary">{t("subscriptionBilling.subscriptionStatus")}</Typography>
            <Typography variant="h6">{billing?.contract?.subscription_status || t("subscriptionBilling.noSubscription")}</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2">{t("subscriptionBilling.coverageUntil")}: {formatDate(billing?.contract?.paid_coverage_until)}</Typography>
            <Typography variant="body2" mt={1}>{t("subscriptionBilling.nextPayment")}: {formatDate(billing?.contract?.next_payment_at)}</Typography>
            <Typography variant="body2" mt={1}>{t("subscriptionBilling.lastPayment")}: {formatDate(billing?.contract?.last_confirmed_payment_at)}</Typography>
              {billing?.contract?.provider_preapproval_id && <Typography variant="body2" mt={1}>{t("subscriptionBilling.providerSync")}: {billing.contract.provider_sync_status}</Typography>}
              {billing?.contract?.pending_monthly_price != null && <Typography variant="body2" mt={1}>{t("subscriptionBilling.scheduledPrice")}: {currency(billing.contract.pending_monthly_price)} {t("subscriptionBilling.fromDate", { date: formatDate(billing.contract.pending_price_effective_at) })}</Typography>}
          </GlassCard></Grid>
          <Grid item xs={12} md={9}><GlassCard hover={false}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
              <Box><Typography variant="subtitle2" color="text.secondary">{initialContract ? t("subscriptionBilling.initialProposal") : t("subscriptionBilling.changeProposal")}</Typography>
                <Typography variant="h4">{quote ? currency(quote.amount, quote.currency) : "--"}</Typography>
                <Typography variant="body2" color="text.secondary">{t("subscriptionBilling.catalogVersion", { version: quote?.catalog_version_number || "--" })}</Typography>
              </Box>
              <Button variant="contained" onClick={submitRequest} disabled={loading || submitting || quoting || !quote || requestInProgress}>
                {submitting ? t("subscriptionBilling.sending") : requestInProgress ? t("subscriptionBilling.requestUnderReview") : initialContract ? t("subscriptionBilling.submitInitial") : t("subscriptionBilling.requestChange")}
              </Button>
              {billing?.contract?.monthly_price > 0 && !billing.contract.provider_preapproval_id && <Button variant="outlined" onClick={startSubscription} disabled={startingSubscription}>
                {startingSubscription ? t("subscriptionBilling.connecting") : t("subscriptionBilling.configureMercadoPago")}
              </Button>}
            </Stack>
            {initialContract && !billing?.contract?.monthly_price && <Typography variant="caption" color="text.secondary" display="block" mt={1}>{t("subscriptionBilling.approvalRequired")}</Typography>}
          </GlassCard></Grid>
        </Grid>
        {loading ? <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><CircularProgress /></Box> : (
          <Grid container spacing={2}>{cameras.map((camera) => {
            const configured = [...new Set(configs.filter((config) => Number(config.camera_id) === Number(camera.id)).map((config) => config.detection_type))].sort();
            const currentItems = activeItemsByCamera.get(Number(camera.id)) || [];
            const selected = selection[camera.id] || { included: false, detections: [] };
            const subtotal = quote?.camera_subtotals?.find((row) => Number(row.camera_id) === Number(camera.id))?.amount ?? null;
            return <Grid item xs={12} md={6} xl={4} key={camera.id}><GlassCard hover={false}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Box><Typography variant="h6">{camera.display_name || camera.name}</Typography><Typography variant="body2" color="text.secondary">{camera.branch_name || t("subscriptionBilling.noBranch")} · {camera.status || t("subscriptionBilling.unknownStatus")}</Typography></Box>
                <Typography variant="subtitle1">{selected.included ? currency(subtotal) : currency(0)}/mes</Typography>
              </Stack>
              <FormControlLabel control={<Checkbox checked={Boolean(selected.included)} onChange={(event) => toggleCamera(camera.id, event.target.checked)} />} label={t("subscriptionBilling.includeCamera", { price: currency(priceByKey.get("camera:") ?? null) })} />
              <Divider sx={{ my: 1 }} /><Typography variant="subtitle2" mb={0.5}>{t("subscriptionBilling.detectionsPerCamera")}</Typography>
              {configured.length ? configured.map((type) => <FormControlLabel key={type} sx={{ display: "flex", justifyContent: "space-between", ml: 0 }}
                control={<Checkbox checked={selected.detections.includes(type)} disabled={!selected.included} onChange={(event) => toggleDetection(camera.id, type, event.target.checked)} />}
                label={<Stack direction="row" justifyContent="space-between" width="100%"><span>{type}</span><span>{currency(priceByKey.get(`detection:${type}`) ?? null)}/mes</span></Stack>}
              />) : <Typography variant="body2" color="text.secondary">{t("subscriptionBilling.noDetections")}</Typography>}
              <Divider sx={{ my: 1 }} /><Typography variant="caption" color="text.secondary">{t("subscriptionBilling.currentContract")}: {currentItems.length ? currentItems.map((item) => item.component_type === "camera" ? t("subscriptionBilling.camera") : item.detection_type).join(", ") : t("subscriptionBilling.noContractSnapshot")}</Typography>
            </GlassCard></Grid>;
          })}</Grid>
        )}
        <GlassCard hover={false}><Typography variant="h6" mb={1}>{t("subscriptionBilling.recentHistory")}</Typography>
          {(billing?.payments || []).length === 0 && (billing?.coverage || []).length === 0 && (billing?.change_requests || []).length === 0
            ? <Typography color="text.secondary">{t("subscriptionBilling.noHistory")}</Typography>
            : <Stack spacing={1}>
              {(billing?.payments || []).map((payment) => <Typography key={`p-${payment.id}`} variant="body2">{t("subscriptionBilling.paymentRecord", { provider: payment.provider, status: payment.status, amount: currency(payment.amount, payment.currency), date: formatDate(payment.created_at) })}</Typography>)}
              {(billing?.coverage || []).map((entry) => <Typography key={`c-${entry.id}`} variant="body2">{t("subscriptionBilling.coverageRecord", { type: entry.entry_type, start: formatDate(entry.period_start), end: formatDate(entry.period_end), status: entry.status })}</Typography>)}
              {(billing?.change_requests || []).map((request) => <Typography key={`r-${request.id}`} variant="body2">{t("subscriptionBilling.requestRecord", { id: request.id, status: request.status, amount: currency(request.quote_snapshot?.amount), date: formatDate(request.created_at) })}</Typography>)}
              {(billing?.notifications || []).map((item) => <Typography key={`n-${item.id}`} variant="body2">{t("subscriptionBilling.notification", { message: item.message, date: formatDate(item.created_at) })}</Typography>)}
            </Stack>}
        </GlassCard>
      </Stack>
    </Box>
  );
};

export default Billing;
